import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './AdminFirmDashboard.module.css';
import { firmApi } from '../../services/firmApi';
import { FirmDetailed } from '../../types/firm';

const AdminFirmDashboard: React.FC = () => {
  const [firms, setFirms] = useState<FirmDetailed[]>([]);
  const [filteredFirms, setFilteredFirms] = useState<FirmDetailed[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const isMountedRef = useRef<boolean>(true);

  // Fetch firms function that can be called from anywhere
  const loadFirms = async () => {
    try {
      console.log('Starting to fetch firms...');
      setLoading(true);
      
      const data = await firmApi.getAllFirms();
      console.log('Fetched firms:', data);
      console.log('Firms array length:', data.length);
      
      if (!Array.isArray(data)) {
        console.error('API did not return an array:', data);
        toast.error('Invalid data format received from the server', {
          position: "top-right",
          autoClose: 5000
        });
        setLoading(false);
        return;
      }
      
      setFirms(data);
      setFilteredFirms(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching firms:', error);
      toast.error('Failed to load firms. Please try again later.', {
        position: "top-right",
        autoClose: 3000
      });
      setLoading(false);
    }
  };

  // Simple effect to fetch firms on mount
  useEffect(() => {
    loadFirms();
  }, []);

  // Force loading to false if we have firms but loading is still true
  useEffect(() => {
    if (firms.length > 0 && loading) {
      console.log('Firms loaded but loading state is still true, forcing loading to false');
      setLoading(false);
    }
  }, [firms, loading]);

  useEffect(() => {
    // Filter firms when search term changes
    console.log('Filtering firms with term:', searchTerm, 'from', firms.length, 'firms');
    
    let filtered = firms;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(firm =>
        (firm.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firm.abbrev || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firm.url || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(firm => firm.status === 1);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(firm => firm.status !== 1);
    }
    
    console.log('Filtered results:', filtered.length, 'firms');
    setFilteredFirms(filtered);
  }, [searchTerm, firms, statusFilter]);

  const handleDeleteFirm = async (firmId: string) => {
    // Instead of window.confirm, use toast.warn with a custom UI for confirmation
    toast.warn(
      <div style={{ minWidth: '300px' }}>
        <p className="mb-3 fw-bold">Are you sure you want to delete this firm?</p>
        <div className="d-flex justify-content-center gap-3">
          <button 
            className="btn btn-secondary px-4" 
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger px-4" 
            onClick={async () => {
              toast.dismiss();
              try {
                await firmApi.deleteFirm(firmId);
                // Remove the firm from state
                setFirms(prevFirms => prevFirms.filter(firm => String(firm.Firm_ID) !== firmId));
                setFilteredFirms(prevFiltered => prevFiltered.filter(firm => String(firm.Firm_ID) !== firmId));
                toast.success('Firm deleted successfully', {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              } catch (error) {
                console.error('Error deleting firm:', error);
                toast.error('Failed to delete firm. Please try again later.', {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
        className: styles.deleteConfirmationToast,
        style: { 
          maxWidth: '400px', 
          width: '100%',
          padding: '10px'
        }
      }
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
      .toString().padStart(2, '0')}.${date.getFullYear()} ${date.getHours().toString()
      .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className={`container mt-5 ${styles.loadingContainer}`}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading firms...</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className="container mt-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">Firm Management</li>
          </ol>
        </nav>

        <div className="mb-4">
          <h2 className="mb-1">Firm Management</h2>
          <p className="text-muted mb-0">
            <i className="bi bi-building me-2"></i>
            Manage firms and organizations
          </p>
        </div>

        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <div className={styles.tableHeaderTop}>
              <div className="d-flex align-items-center">
                <i className="bi bi-table me-2 text-primary"></i>
                <h5 className="mb-0">Firms</h5>
              </div>
              <div className={styles.headerActions}>
                <span className="badge bg-primary rounded-pill">
                  {filteredFirms.length} {filteredFirms.length === 1 ? 'Firm' : 'Firms'}
                </span>
                <button 
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => loadFirms()}
                  title="Refresh firms"
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Refresh
                </button>
                <Link 
                  className="btn btn-primary btn-sm" 
                  to="/admin/firms/create"
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  New Firm
                </Link>
              </div>
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="p-3 border-bottom bg-white">
            <div className="input-group mb-3">
              <span className="input-group-text bg-white">
                <i className="bi bi-search"></i>
              </span>
              <input 
                type="text"
                className="form-control"
                placeholder="Search by name, abbreviation or URL..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  className="btn btn-outline-secondary"
                  onClick={() => setSearchTerm('')}
                  title="Clear search"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
            
            {/* Status Filter */}
            <div className="btn-group w-100" role="group">
              <button
                type="button"
                className={`btn ${statusFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setStatusFilter('all')}
              >
                <i className="bi bi-building me-1"></i>
                All Firms
                <span className="badge bg-white text-primary ms-2">{firms.length}</span>
              </button>
              <button
                type="button"
                className={`btn ${statusFilter === 'active' ? 'btn-success' : 'btn-outline-success'}`}
                onClick={() => setStatusFilter('active')}
              >
                <i className="bi bi-check-circle me-1"></i>
                Active
                <span className={`badge ${statusFilter === 'active' ? 'bg-white text-success' : 'bg-success text-white'} ms-2`}>
                  {firms.filter(f => f.status === 1).length}
                </span>
              </button>
              <button
                type="button"
                className={`btn ${statusFilter === 'inactive' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={() => setStatusFilter('inactive')}
              >
                <i className="bi bi-x-circle me-1"></i>
                Inactive
                <span className={`badge ${statusFilter === 'inactive' ? 'bg-white text-secondary' : 'bg-secondary text-white'} ms-2`}>
                  {firms.filter(f => f.status !== 1).length}
                </span>
              </button>
            </div>
          </div>
          
          {/* Desktop Table View */}
          <div className={styles.desktopTable}>
            <table className={`table ${styles.firmTable}`}>
              <thead>
                <tr>
                  <th>
                    <i className="bi bi-building me-1"></i>
                    Firm
                  </th>
                  <th>
                    <i className="bi bi-link-45deg me-1"></i>
                    URL
                  </th>
                  <th>
                    <i className="bi bi-calendar-plus me-1"></i>
                    Created
                  </th>
                  <th>
                    <i className="bi bi-calendar-check me-1"></i>
                    Updated
                  </th>
                  <th>
                    <i className="bi bi-tools me-1"></i>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredFirms.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center p-5">
                      <div className={styles.emptyState}>
                        {searchTerm ? (
                          <>
                            <i className="bi bi-search display-4 text-muted mb-3"></i>
                            <h5 className="text-muted">No firms found</h5>
                            <p className="text-muted mb-3">
                              Try adjusting your search criteria.
                            </p>
                            <button 
                              className="btn btn-outline-secondary"
                              onClick={() => setSearchTerm('')}
                            >
                              <i className="bi bi-x-circle me-2"></i>
                              Clear Search
                            </button>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-inbox display-4 text-muted mb-3"></i>
                            <h5 className="text-muted">No firms configured</h5>
                            <p className="text-muted mb-3">
                              Create your first firm to get started.
                            </p>
                            <Link 
                              className="btn btn-primary"
                              to="/admin/firms/create"
                            >
                              <i className="bi bi-plus-circle me-2"></i>
                              Create First Firm
                            </Link>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredFirms.map((firm, index) => (
                    <tr key={firm.Firm_ID} className={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
                      <td className={styles.firmTableCell}>
                        <div className={styles.firmInfo}>
                          {firm.logo ? (
                            <img
                              src={`data:image/png;base64,${firm.logo}`}
                              alt={`${firm.name} logo`}
                              className={styles.firmLogo}
                            />
                          ) : (
                            <div className={styles.firmLogoPlaceholder}>
                              <i className="bi bi-building"></i>
                            </div>
                          )}
                          <div>
                            <a 
                              href="#" 
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(`/admin/firms/${String(firm.Firm_ID)}`);
                              }}
                              className={styles.firmLink}
                            >
                              <strong>{firm.name}</strong>
                              {firm.status !== 1 && (
                                <span className="badge bg-secondary ms-2">Inactive</span>
                              )}
                            </a>
                            <div>
                              <span className={styles.abbrevBadge}>{firm.abbrev}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={styles.firmTableCell}>
                        {firm.url ? (
                          <a href={firm.url} target="_blank" rel="noopener noreferrer" className={styles.urlLink}>
                            <i className="bi bi-box-arrow-up-right me-1"></i>
                            {firm.url.replace(/^https?:\/\//, '')}
                          </a>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className={styles.firmTableCell}>
                        <small className="text-muted">{formatDate(firm.created_at)}</small>
                      </td>
                      <td className={styles.firmTableCell}>
                        <small className="text-muted">{formatDate(firm.updated_at)}</small>
                      </td>
                      <td className={`${styles.firmTableCell} text-end`}>
                        <div className={styles.actionButtons}>
                          <button 
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate(`/admin/firms/${String(firm.Firm_ID)}`)}
                            title="Edit firm"
                          >
                            <i className="bi bi-pencil-fill"></i>
                          </button>
                          <button 
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteFirm(String(firm.Firm_ID))}
                            title="Delete firm"
                          >
                            <i className="bi bi-trash-fill"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className={styles.mobileCards}>
            {filteredFirms.length === 0 ? (
              <div className="text-center p-5">
                <div className={styles.emptyState}>
                  {searchTerm ? (
                    <>
                      <i className="bi bi-search display-4 text-muted mb-3"></i>
                      <h5 className="text-muted">No firms found</h5>
                      <p className="text-muted mb-3">
                        Try adjusting your search criteria.
                      </p>
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={() => setSearchTerm('')}
                      >
                        <i className="bi bi-x-circle me-2"></i>
                        Clear Search
                      </button>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-inbox display-4 text-muted mb-3"></i>
                      <h5 className="text-muted">No firms configured</h5>
                      <p className="text-muted mb-3">
                        Create your first firm to get started.
                      </p>
                      <Link 
                        className="btn btn-primary"
                        to="/admin/firms/create"
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Create First Firm
                      </Link>
                    </>
                  )}
                </div>
              </div>
            ) : (
              filteredFirms.map((firm) => (
                <div key={firm.Firm_ID} className={styles.firmCard}>
                  <div className={styles.firmCardHeader}>
                    <div className="d-flex align-items-center mb-2">
                      {firm.logo ? (
                        <img
                          src={`data:image/png;base64,${firm.logo}`}
                          alt={`${firm.name} logo`}
                          className={styles.firmLogoCard}
                        />
                      ) : (
                        <div className={styles.firmLogoPlaceholderCard}>
                          <i className="bi bi-building"></i>
                        </div>
                      )}
                      <div className="flex-grow-1">
                        <a 
                          href="#" 
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/admin/firms/${String(firm.Firm_ID)}`);
                          }}
                          className={styles.firmLink}
                        >
                          <h6 className="mb-0 fw-bold">
                            {firm.name}
                            {firm.status !== 1 && (
                              <span className="badge bg-secondary ms-2">Inactive</span>
                            )}
                          </h6>
                        </a>
                        <span className={styles.abbrevBadge}>{firm.abbrev}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.firmCardBody}>
                    {firm.url && (
                      <div className="mb-2">
                        <small className="text-muted">
                          <i className="bi bi-link-45deg me-1"></i>
                          URL:
                        </small>
                        <div className="mt-1">
                          <a href={firm.url} target="_blank" rel="noopener noreferrer" className={styles.urlLink}>
                            <i className="bi bi-box-arrow-up-right me-1"></i>
                            {firm.url.replace(/^https?:\/\//, '')}
                          </a>
                        </div>
                      </div>
                    )}
                    <div className="mb-2">
                      <small className="text-muted">
                        <i className="bi bi-calendar-plus me-1"></i>
                        Created: {formatDate(firm.created_at)}
                      </small>
                    </div>
                    <div>
                      <small className="text-muted">
                        <i className="bi bi-calendar-check me-1"></i>
                        Updated: {formatDate(firm.updated_at)}
                      </small>
                    </div>
                  </div>

                  <div className={styles.firmCardActions}>
                    <button 
                      className="btn btn-sm btn-outline-primary flex-fill"
                      onClick={() => navigate(`/admin/firms/${String(firm.Firm_ID)}`)}
                      title="Edit Firm"
                    >
                      <i className="bi bi-pencil-fill me-1"></i>
                      Edit
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-danger flex-fill" 
                      onClick={() => handleDeleteFirm(String(firm.Firm_ID))}
                      title="Delete Firm"
                    >
                      <i className="bi bi-trash-fill me-1"></i>
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
 
  );
};

export default AdminFirmDashboard;
